import React, { useCallback, useEffect, useMemo } from 'react';
import { Image, View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { useAppDispatch } from 'store/hooks';
import { AppView, CardView, NavigationHeader, Spacer } from 'design-system';
import BorderedSquircle from 'design-system/BorderedSquircle';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import useColorScheme from 'hooks/useColorScheme';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import { postFunnelEvents } from 'features/funnel-events/actions';
import { SaveFunnelEvents } from 'features/funnel-events/types';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import Amplitude from 'utils/amplitude';
import createStyleSheets from 'utils/createStyleSheets';
import { OnboardingFlow } from 'utils/types/navigation';
const autoSaveSavingsImg = require('../../img/autosave_growth_graphics.png');
const autoSaveSavingsDarkImg = require('../../img/autosave_growth_graphics_dark.png');
const AutosaveOnboardingIntroScreen = ({ route, navigation }) => {
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const dispatch = useAppDispatch();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const colorScheme = useColorScheme();
    const { paddingHorizontalStyle } = useAppFrameDimensions(mountedOnAnotherScreen);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    useEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        if (!mountedOnAnotherScreen) {
            Amplitude.logEvent('How Direct Debit Works', { source: 'save' });
        }
    }, [mountedOnAnotherScreen]);
    const outputColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const scrollY = useSharedValue(0);
    const scrollHandler = useAnimatedScrollHandler((event) => {
        scrollY.value = event.contentOffset.y;
    });
    const imgSource = useMemo(() => {
        if (colorScheme === 'dark') {
            return autoSaveSavingsDarkImg;
        }
        return autoSaveSavingsImg;
    }, [colorScheme]);
    const squircleProps = useMemo(() => ({ stroke: colors.text.brand }), [colors.text.brand]);
    const handleNext = useCallback(() => {
        dispatch(postFunnelEvents([SaveFunnelEvents.ONB_SAVE_NEXT_FREQUENCY]));
        navigation.navigate('AutosaveChoosePeriod');
    }, []);
    return (<AppView type="secondary" withFrame={false}>
      <NavigationHeader scrollY={scrollY} regularTitleHeight outputColors={outputColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Pots}/> : undefined} rightContent={<View style={mountedOnAnotherScreen ? styles.rightContent : null}>
            <HelpScreenButton />
          </View>}/>
      <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title="Let’s set up Autosave" subTitle="Autosavers save 10x more than manual savers"/>
        <CardView withPadding noMarginTop noMarginBottom>
          <View style={styles.badge}>
            <Text Text-16 OnBrand>
              Money saved
            </Text>
          </View>
          <Image source={imgSource} style={styles.img}/>
          <Spacer h={12}/>
          <View style={styles.row}>
            <View style={styles.flexEnd}>
              <Text Text-14>Without Autosave</Text>
            </View>
            <View style={styles.flex}>
              <Text Text-14>With Autosave</Text>
            </View>
          </View>
        </CardView>

        <CardView>
          <View style={styles.infoCard}>
            <BorderedSquircle size={24} borderStrokeProps={squircleProps}>
              <Text Text-14 Primary>
                1
              </Text>
            </BorderedSquircle>
            <View style={styles.flex}>
              <Text Text-16>Pick your saving rules</Text>
              <Spacer h={4}/>
              <Text TextThin-14 Secondary>
                Round up change, set a fixed amount, or let Emma choose for you
              </Text>
            </View>
          </View>

          <View style={styles.infoCard}>
            <BorderedSquircle size={24} borderStrokeProps={squircleProps}>
              <Text Text-14 Primary>
                2
              </Text>
            </BorderedSquircle>
            <View style={styles.flex}>
              <Text Text-16>Set up Direct Debit</Text>
              <Spacer h={4}/>
              <Text TextThin-14 Secondary>
                Choose where money gets saved from to set up a Direct Debit
              </Text>
            </View>
          </View>

          <View style={styles.infoCard}>
            <BorderedSquircle size={24} borderStrokeProps={squircleProps}>
              <Text Text-14 Primary>
                3
              </Text>
            </BorderedSquircle>
            <View style={styles.flex}>
              <Text Text-16>Stay in control</Text>
              <Spacer h={4}/>
              <Text TextThin-14 Secondary>
                Pause anytime. Skip payments. We notify you 24h in advance
              </Text>
            </View>
          </View>
        </CardView>
      </Animated.ScrollView>
      <Button brand floating title="Get started" isInTab={!!mountedOnAnotherScreen} onPress={handleNext}/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    rightContent: {
        marginRight: rem(16),
        marginBottom: rem(6),
        alignItems: 'flex-end',
    },
    row: {
        gap: rem(16),
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    flexEnd: {
        flex: 1,
        alignItems: 'flex-end',
    },
    img: {
        width: '100%',
        aspectRatio: 2.04,
        alignSelf: 'center',
    },
    badge: {
        zIndex: 1,
        top: rem(16),
        left: rem(16),
        position: 'absolute',
        borderRadius: rem(8),
        paddingVertical: rem(8),
        paddingHorizontal: rem(16),
        backgroundColor: colors.buttons.brandGroup,
    },
    infoCard: {
        gap: rem(16),
        padding: rem(16),
        flexDirection: 'row',
    },
}));
export default AutosaveOnboardingIntroScreen;
